// Funding rounds management page
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { supabase } from '@/lib/supabase-auth'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { usePermissions, useUser } from '@/lib/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import InvestmentForm from '@/components/custom-ui/investment-form'
import InvestmentList from '@/components/custom-ui/investment-list'
import SecondaryTransactionForm from '@/components/custom-ui/secondary-transaction-form'
import { InvestmentCalculator } from '@/lib/investment-calculator'
import { 
  DollarSign, 
  Plus, 
  TrendingUp, 
  Calendar,
  Edit2,
  Trash2,
  Users,
  FileText,
  TrendingDown,
  Info,
  UserPlus,
  Building,
  ChevronRight
} from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'

interface FundingRound {
  id: string
  round_name: string
  round_date: string
  pre_money_valuation: number | null
  post_money_valuation: number | null
  target_amount: number | null
  allow_secondary_sales?: boolean | null
  total_investment_committed?: number | null
  total_investment_received?: number | null
  investor_count?: number | null
  total_secondary_shares?: number | null
  total_secondary_amount?: number | null
  created_at: string | null
  [key: string]: any
}

export default function FundingRoundsPage() {
  const getToken = useGetToken()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingRound, setEditingRound] = useState<FundingRound | null>(null)
  const [selectedRound, setSelectedRound] = useState<FundingRound | null>(null)
  const [showInvestmentForm, setShowInvestmentForm] = useState(false)
  const [showSecondaryForm, setShowSecondaryForm] = useState(false)
  const queryClient = useQueryClient()
  const { currentProject } = useProject()
  const permissions = usePermissions()
  const { profile: user } = useUser()

  // Form state
  const [roundName, setRoundName] = useState('')
  const [roundType, setRoundType] = useState('seed')
  const [roundDate, setRoundDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [preMoneyValuation, setPreMoneyValuation] = useState('')
  const [targetAmount, setTargetAmount] = useState('')
  const [sharePrice, setSharePrice] = useState('')
  const [allowSecondary, setAllowSecondary] = useState(false)

  const { data: rounds, isLoading } = useQuery<FundingRound[]>({
    queryKey: ['funding-rounds', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('funding_rounds')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('round_date', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  // Get current total shares for price calculation
  const { data: totalShares } = useQuery({
    queryKey: ['total-shares', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return 0

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .from('cap_table_entries')
        .select('shares')
        .eq('project_id', currentProject.id)

      if (error) throw error
      return data?.reduce((sum, entry) => sum + entry.shares, 0) || 0
    },
    enabled: !!currentProject
  })

  const { mutate: saveFundingRound, isPending: isSaving } = useMutation({
    mutationFn: async (roundData: {
      round_name: string
      round_type: string
      round_date: string
      pre_money_valuation: number
      post_money_valuation: number
      target_amount?: number
      share_price?: number
      allow_secondary_sales: boolean
      id?: string
    }) => {
      if (roundData.id) {
        const { error } = await supabase
          .from('funding_rounds')
          .update({
            round_name: roundData.round_name,
            round_type: roundData.round_type,
            round_date: roundData.round_date,
            pre_money_valuation: roundData.pre_money_valuation,
            post_money_valuation: roundData.post_money_valuation,
            target_amount: roundData.target_amount,
            share_price: roundData.share_price,
            allow_secondary_sales: roundData.allow_secondary_sales,
          })
          .eq('id', roundData.id)

        if (error) throw error
      } else {
        if (!currentProject) throw new Error('No project selected')
        
        const { error } = await supabase
          .from('funding_rounds')
          .insert({
            ...roundData,
            project_id: currentProject.id,
            amount_raised: roundData.post_money_valuation - roundData.pre_money_valuation
          })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funding-rounds'] })
      handleCloseDialog()
      toast.success(editingRound ? 'Funding round updated' : 'Funding round created')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  const { mutate: deleteFundingRound } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('funding_rounds')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funding-rounds'] })
      toast.success('Funding round deleted')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })

  const handleOpenDialog = (round?: FundingRound) => {
    if (round) {
      setEditingRound(round)
      setRoundName(round.round_name)
      setRoundType(round.round_type || 'seed')
      setRoundDate(format(new Date(round.round_date), 'yyyy-MM-dd'))
      setPreMoneyValuation((round.pre_money_valuation || 0).toString())
      const investment = (round.post_money_valuation || 0) - (round.pre_money_valuation || 0)
      setTargetAmount(round.target_amount?.toString() || investment.toString())
      setSharePrice('')
      setAllowSecondary(round.allow_secondary_sales || false)
    } else {
      setEditingRound(null)
      setRoundName('')
      setRoundType('seed')
      setRoundDate(format(new Date(), 'yyyy-MM-dd'))
      setPreMoneyValuation('')
      setTargetAmount('')
      setSharePrice('')
      setAllowSecondary(false)
    }
    setShowAddDialog(true)
  }

  const handleCloseDialog = () => {
    setShowAddDialog(false)
    setEditingRound(null)
    setRoundName('')
    setPreMoneyValuation('')
    setTargetAmount('')
    setSharePrice('')
  }

  const handleSave = () => {
    const preMoney = parseFloat(preMoneyValuation)
    const target = parseFloat(targetAmount)
    const postMoney = preMoney + target
    
    // Calculate share price if not provided
    let calculatedSharePrice = parseFloat(sharePrice)
    if (!calculatedSharePrice && totalShares) {
      calculatedSharePrice = InvestmentCalculator.calculatePricePerShare(preMoney, totalShares)
    }

    saveFundingRound({
      round_name: roundName,
      round_type: roundType,
      round_date: roundDate,
      pre_money_valuation: preMoney,
      post_money_valuation: postMoney,
      target_amount: target,
      share_price: calculatedSharePrice,
      allow_secondary_sales: allowSecondary,
      id: editingRound?.id
    })
  }

  const calculateDilution = (round: FundingRound) => {
    const postMoney = round.post_money_valuation || 0
    const preMoney = round.pre_money_valuation || 0
    if (postMoney === 0) return '0.00'
    const investment = postMoney - preMoney
    const dilutionPercent = (investment / postMoney) * 100
    return dilutionPercent.toFixed(2)
  }

  const formatCurrency = (value: number | null | undefined) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0)
  }

  const getRoundTypeBadge = (type?: string) => {
    const colors: Record<string, string> = {
      'pre-seed': 'bg-gray-100 text-gray-800',
      'seed': 'bg-green-100 text-green-800',
      'series-a': 'bg-blue-100 text-blue-800',
      'series-b': 'bg-purple-100 text-purple-800',
      'series-c': 'bg-yellow-100 text-yellow-800',
      'bridge': 'bg-orange-100 text-orange-800',
      'other': 'bg-gray-100 text-gray-800'
    }

    return (
      <Badge className={colors[type || 'other'] || colors.other}>
        {type?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Other'}
      </Badge>
    )
  }

  if (!selectedRound) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Funding Rounds</h1>
            <p className="mt-1 text-sm text-gray-600">
              Track funding events and investor contributions
            </p>
          </div>
          {permissions.canEditProject && (
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Round
            </Button>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Raised</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(
                    rounds?.reduce((sum, round) => 
                      sum + (round.total_investment_received || 0), 0
                    ) || 0
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Current Valuation</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {rounds && rounds.length > 0
                    ? formatCurrency(rounds[0].post_money_valuation)
                    : '$0'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Investors</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {rounds?.reduce((sum, round) => sum + (round.investor_count || 0), 0) || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Funding Rounds</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {rounds?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Funding Rounds Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Round
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pre-Money
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Raised
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Post-Money
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    <div className="animate-pulse">Loading rounds...</div>
                  </td>
                </tr>
              ) : rounds?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    No funding rounds yet. Add your first round to track investments.
                  </td>
                </tr>
              ) : (
                rounds?.map((round) => (
                  <tr key={round.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {round.round_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {round.investor_count || 0} investors
                          </div>
                        </div>
                        {getRoundTypeBadge(round.round_type)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(round.round_date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(round.pre_money_valuation)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <div>
                        <div className="font-medium text-green-600">
                          {formatCurrency(round.total_investment_received || 0)}
                        </div>
                        {round.total_investment_committed && round.total_investment_committed > (round.total_investment_received || 0) && (
                          <div className="text-xs text-gray-500">
                            of {formatCurrency(round.total_investment_committed)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(round.post_money_valuation)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center space-y-1">
                        {round.total_investment_committed && round.total_investment_received ? (
                          <div className="text-xs">
                            <div className="font-medium">
                              {Math.round((round.total_investment_received / round.total_investment_committed) * 100)}%
                            </div>
                            <div className="text-gray-500">funded</div>
                          </div>
                        ) : (
                          <Badge variant="outline">Open</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedRound(round)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                        {permissions.canEditProject && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(round)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (confirm('Are you sure you want to delete this funding round? This will also delete all associated investments.')) {
                                  deleteFundingRound(round.id)
                                }
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingRound ? 'Edit Funding Round' : 'Add Funding Round'}
              </DialogTitle>
              <DialogDescription>
                Record a funding event to track investments and dilution
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="roundName">Round Name</Label>
                  <Input
                    id="roundName"
                    value={roundName}
                    onChange={(e) => setRoundName(e.target.value)}
                    placeholder="e.g., Seed, Series A"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roundType">Round Type</Label>
                  <Select value={roundType} onValueChange={setRoundType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pre-seed">Pre-Seed</SelectItem>
                      <SelectItem value="seed">Seed</SelectItem>
                      <SelectItem value="series-a">Series A</SelectItem>
                      <SelectItem value="series-b">Series B</SelectItem>
                      <SelectItem value="series-c">Series C</SelectItem>
                      <SelectItem value="bridge">Bridge</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="roundDate">Date</Label>
                <Input
                  id="roundDate"
                  type="date"
                  value={roundDate}
                  onChange={(e) => setRoundDate(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="preMoneyValuation">Pre-Money Valuation ($)</Label>
                  <Input
                    id="preMoneyValuation"
                    type="number"
                    value={preMoneyValuation}
                    onChange={(e) => setPreMoneyValuation(e.target.value)}
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="targetAmount">Target Amount ($)</Label>
                  <Input
                    id="targetAmount"
                    type="number"
                    value={targetAmount}
                    onChange={(e) => setTargetAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sharePrice">Share Price ($)</Label>
                <Input
                  id="sharePrice"
                  type="number"
                  step="0.0001"
                  value={sharePrice}
                  onChange={(e) => setSharePrice(e.target.value)}
                  placeholder="Leave blank to calculate automatically"
                />
                {!sharePrice && preMoneyValuation && totalShares && (
                  <p className="text-sm text-gray-600">
                    Will be calculated as: ${(parseFloat(preMoneyValuation) / totalShares).toFixed(4)} per share
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allowSecondary"
                  checked={allowSecondary}
                  onCheckedChange={(checked) => setAllowSecondary(checked as boolean)}
                />
                <Label 
                  htmlFor="allowSecondary" 
                  className="text-sm font-normal cursor-pointer"
                >
                  Allow secondary sales (employees can sell shares in this round)
                </Label>
              </div>

              {preMoneyValuation && targetAmount && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>Post-Money Valuation:</span>
                        <span className="font-medium">
                          {formatCurrency(
                            parseFloat(preMoneyValuation) + parseFloat(targetAmount)
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>New Investor Ownership:</span>
                        <span className="font-medium">
                          {(
                            (parseFloat(targetAmount) / 
                            (parseFloat(preMoneyValuation) + parseFloat(targetAmount))) * 100
                          ).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog} disabled={isSaving}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={isSaving || !roundName || !preMoneyValuation || !targetAmount}
              >
                {isSaving ? 'Saving...' : editingRound ? 'Update' : 'Create'} Round
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Detail View for Selected Round
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedRound(null)}
          >
            ← Back to Rounds
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {selectedRound.round_name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {format(new Date(selectedRound.round_date), 'MMMM d, yyyy')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRound.allow_secondary_sales && user && (
            <Button
              variant="outline"
              onClick={() => setShowSecondaryForm(true)}
            >
              <TrendingDown className="h-4 w-4 mr-2" />
              Sell My Shares
            </Button>
          )}
          {permissions.canEditProject && (
            <Button onClick={() => setShowInvestmentForm(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Record Investment
            </Button>
          )}
        </div>
      </div>

      {/* Round Summary */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Round Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500">Pre-Money Valuation</p>
            <p className="text-xl font-semibold">
              {formatCurrency(selectedRound.pre_money_valuation)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Amount Raised</p>
            <p className="text-xl font-semibold text-green-600">
              {formatCurrency(selectedRound.total_investment_received || 0)}
            </p>
            {selectedRound.total_investment_committed && selectedRound.total_investment_committed > (selectedRound.total_investment_received || 0) && (
              <p className="text-sm text-gray-500">
                of {formatCurrency(selectedRound.total_investment_committed)} committed
              </p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500">Post-Money Valuation</p>
            <p className="text-xl font-semibold">
              {formatCurrency(selectedRound.post_money_valuation)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Share Price</p>
            <p className="text-xl font-semibold">
              ${selectedRound.share_price?.toFixed(4) || '0.00'}
            </p>
          </div>
        </div>
        
        {selectedRound.allow_secondary_sales && (selectedRound.total_secondary_shares || 0) > 0 && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-gray-500">Secondary Sales</p>
            <p className="text-lg font-medium">
              {(selectedRound.total_secondary_shares || 0).toLocaleString()} shares
              ({formatCurrency(selectedRound.total_secondary_amount || 0)})
            </p>
          </div>
        )}
      </div>

      {/* Investments Tab */}
      <Tabs defaultValue="investments" className="space-y-4">
        <TabsList>
          <TabsTrigger value="investments">
            <Building className="h-4 w-4 mr-2" />
            Investments ({selectedRound.investor_count || 0})
          </TabsTrigger>
          {selectedRound.allow_secondary_sales && (
            <TabsTrigger value="secondary">
              <TrendingDown className="h-4 w-4 mr-2" />
              Secondary Sales
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="investments">
          <InvestmentList
            fundingRoundId={selectedRound.id}
            projectId={currentProject?.id || ''}
          />
        </TabsContent>

        <TabsContent value="secondary">
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="text-center text-gray-500">
              Secondary transaction tracking coming soon
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Investment Form Modal */}
      {showInvestmentForm && currentProject && (
        <InvestmentForm
          open={showInvestmentForm}
          onOpenChange={setShowInvestmentForm}
          fundingRoundId={selectedRound.id}
          projectId={currentProject.id}
          pricePerShare={selectedRound.share_price}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['funding-rounds'] })
          }}
        />
      )}

      {/* Secondary Transaction Form Modal */}
      {showSecondaryForm && currentProject && (
        <SecondaryTransactionForm
          open={showSecondaryForm}
          onOpenChange={setShowSecondaryForm}
          fundingRoundId={selectedRound.id}
          projectId={currentProject.id}
          pricePerShare={selectedRound.share_price || 0}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['secondary-transactions'] })
          }}
        />
      )}
    </div>
  )
}
